import { useState } from "react";
import { alarmRecords, AlarmRecord, AlarmLevel, AlarmStatus, AlarmType } from "@/mocks/alarm";

const levelColors: Record<AlarmLevel, string> = {
  紧急: "bg-red-100 text-red-700 border-red-200",
  严重: "bg-orange-100 text-orange-700 border-orange-200",
  警告: "bg-amber-100 text-amber-700 border-amber-200",
  提示: "bg-blue-100 text-blue-700 border-blue-200",
};

const levelDot: Record<AlarmLevel, string> = {
  紧急: "bg-red-500",
  严重: "bg-orange-500",
  警告: "bg-amber-500",
  提示: "bg-blue-400",
};

const statusColors: Record<AlarmStatus, string> = {
  未处理: "bg-red-50 text-red-600",
  处理中: "bg-blue-50 text-blue-600",
  已处理: "bg-emerald-50 text-emerald-600",
  已忽略: "bg-gray-100 text-gray-400",
};

const typeIcons: Record<AlarmType, string> = {
  泄漏报警: "ri-fire-line",
  欠费预警: "ri-money-cny-circle-line",
  设备异常: "ri-tools-line",
  压力异常: "ri-pulse-line",
  流量异常: "ri-water-flash-line",
  安检逾期: "ri-shield-cross-line",
  保险到期: "ri-shield-line",
  通信中断: "ri-wifi-off-line",
};

interface HandleModalProps {
  alarm: AlarmRecord;
  onClose: () => void;
  onSubmit: (id: string, note: string, handler: string) => void;
}

function HandleModal({ alarm, onClose, onSubmit }: HandleModalProps) {
  const [note, setNote] = useState("");
  const [handler, setHandler] = useState("张工程师");

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-[480px] max-w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900">处理告警</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="p-3 bg-gray-50 rounded-xl mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${levelColors[alarm.level]}`}>{alarm.level}</span>
            <span className="text-xs font-semibold text-gray-800">{alarm.type}</span>
          </div>
          <p className="text-xs text-gray-500">{alarm.message}</p>
          <p className="text-xs text-gray-400 mt-1">{alarm.userName} · {alarm.occurTime}</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">处理人员</label>
            <input
              value={handler}
              onChange={e => setHandler(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">处理备注 <span className="text-red-400">*</span></label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="请描述处理过程和结果..."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 resize-none"
            />
            <p className="text-xs text-gray-300 text-right">{note.length}/500</p>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-sm text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
          <button
            onClick={() => { if (note.trim()) { onSubmit(alarm.id, note, handler); onClose(); } }}
            disabled={!note.trim()}
            className="flex-1 py-2.5 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] disabled:opacity-40 cursor-pointer whitespace-nowrap"
          >
            确认处理
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  filterLevel: string;
  filterStatus: string;
  filterType: string;
  searchText: string;
}

export default function AlarmList({ filterLevel, filterStatus, filterType, searchText }: Props) {
  const [records, setRecords] = useState<AlarmRecord[]>(alarmRecords);
  const [handlingAlarm, setHandlingAlarm] = useState<AlarmRecord | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = records.filter(a => {
    const matchLevel = filterLevel === "全部" || a.level === filterLevel;
    const matchStatus = filterStatus === "全部" || a.status === filterStatus;
    const matchType = filterType === "全部" || a.type === filterType;
    const matchSearch = !searchText || a.userName.includes(searchText) || a.message.includes(searchText) || a.id.includes(searchText);
    return matchLevel && matchStatus && matchType && matchSearch;
  });

  const handleSubmit = (id: string, note: string, handler: string) => {
    setRecords(prev => prev.map(a =>
      a.id === id
        ? { ...a, status: "已处理" as AlarmStatus, handleNote: note, handler, handleTime: new Date().toLocaleString("zh-CN") }
        : a
    ));
  };

  const handleIgnore = (id: string) => {
    setRecords(prev => prev.map(a => a.id === id ? { ...a, status: "已忽略" as AlarmStatus } : a));
  };

  return (
    <>
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-300">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
              <i className="ri-inbox-line text-4xl"></i>
            </div>
            <p className="text-sm">暂无符合条件的告警记录</p>
          </div>
        )}
        {filtered.map(alarm => (
          <div
            key={alarm.id}
            className={`bg-white rounded-xl border transition-all ${
              alarm.level === "紧急" && alarm.status === "未处理"
                ? "border-red-200 bg-red-50/30"
                : "border-gray-100"
            }`}
          >
            <div
              className="flex items-center gap-4 p-4 cursor-pointer"
              onClick={() => setExpandedId(expandedId === alarm.id ? null : alarm.id)}
            >
              {/* Level dot */}
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${levelDot[alarm.level]} ${alarm.status === "未处理" ? "animate-pulse" : ""}`}></div>

              {/* Type icon */}
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                alarm.level === "紧急" ? "bg-red-100" : alarm.level === "严重" ? "bg-orange-100" : "bg-amber-50"
              }`}>
                <i className={`${typeIcons[alarm.type]} text-base ${
                  alarm.level === "紧急" ? "text-red-600" : alarm.level === "严重" ? "text-orange-600" : "text-amber-600"
                }`}></i>
              </div>

              {/* Main info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${levelColors[alarm.level]}`}>{alarm.level}</span>
                  <span className="text-xs font-semibold text-gray-800">{alarm.type}</span>
                  <span className="text-xs text-gray-400">·</span>
                  <span className="text-xs text-gray-500">{alarm.userName}</span>
                  <span className="text-xs text-gray-300">({alarm.userCategory})</span>
                </div>
                <p className="text-xs text-gray-600 truncate">{alarm.message}</p>
                <p className="text-xs text-gray-400 mt-0.5">{alarm.occurTime} · {alarm.deviceType} · {alarm.deviceId}</p>
              </div>

              {/* Value */}
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-400">检测值</p>
                <p className="text-sm font-bold text-gray-800">{alarm.value}</p>
                <p className="text-xs text-gray-300">阈值: {alarm.threshold}</p>
              </div>

              {/* Status */}
              <div className="flex-shrink-0">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[alarm.status]}`}>
                  {alarm.status}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                {(alarm.status === "未处理" || alarm.status === "处理中") && (
                  <>
                    <button
                      onClick={() => setHandlingAlarm(alarm)}
                      className="px-3 py-1.5 text-xs bg-[#E8531A] text-white rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap"
                    >
                      处理
                    </button>
                    <button
                      onClick={() => handleIgnore(alarm.id)}
                      className="px-3 py-1.5 text-xs bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                    >
                      忽略
                    </button>
                  </>
                )}
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={expandedId === alarm.id ? "ri-arrow-up-s-line text-gray-400" : "ri-arrow-down-s-line text-gray-400"}></i>
                </div>
              </div>
            </div>

            {/* Expanded detail */}
            {expandedId === alarm.id && (
              <div className="px-4 pb-4 border-t border-gray-50 pt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 mb-2">告警详情</h4>
                    <div className="space-y-1.5">
                      {[
                        { label: "告警编号", value: alarm.id },
                        { label: "用户地址", value: alarm.address },
                        { label: "设备编号", value: alarm.deviceId },
                        { label: "推送状态", value: alarm.pushSent ? "已推送" : "未推送" },
                      ].map(item => (
                        <div key={item.label} className="flex gap-2 text-xs">
                          <span className="text-gray-400 w-16 flex-shrink-0">{item.label}</span>
                          <span className="text-gray-700">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {alarm.handleNote && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">处理记录</h4>
                      <div className="space-y-1.5">
                        {[
                          { label: "处理人员", value: alarm.handler ?? "-" },
                          { label: "处理时间", value: alarm.handleTime ?? "-" },
                          { label: "处理备注", value: alarm.handleNote },
                        ].map(item => (
                          <div key={item.label} className="flex gap-2 text-xs">
                            <span className="text-gray-400 w-16 flex-shrink-0">{item.label}</span>
                            <span className="text-gray-700">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {handlingAlarm && (
        <HandleModal
          alarm={handlingAlarm}
          onClose={() => setHandlingAlarm(null)}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}
